import {
	PEOPLE_COMPANY_DOCUMENT_CATEGORY,
	PEOPLE_EMPLOYEE_EVENT_REVIEW_REQUIRED_TYPES,
	PEOPLE_EMPLOYEE_EVENT_REVIEW_STATUS,
	PEOPLE_EMPLOYEE_EVENT_TYPE,
	PEOPLE_EMPLOYEE_WARNING_STATUS,
	PEOPLE_EMPLOYEE_WARNING_TYPE,
	PEOPLE_EMPLOYEE_STATUS,
	PEOPLE_EMPLOYMENT_CHANGE_DOCUMENT_MODE,
	PEOPLE_EMPLOYMENT_CHANGE_STATUS,
	PEOPLE_EMPLOYMENT_CHANGE_TYPE,
	PEOPLE_EMPLOYMENT_TYPE,
	PEOPLE_PERFORMANCE_REVIEW_OUTCOME,
	PEOPLE_PERFORMANCE_REVIEW_STATUS,
	PEOPLE_PERFORMANCE_REVIEW_TYPE,
	PEOPLE_LEAVE_ENTITLEMENT,
	PEOPLE_OPEN_SHIFT_APPLICATION_STATUS,
	PEOPLE_ROSTER_AREA,
	PEOPLE_ROSTER_SHIFT_STATUS,
	PEOPLE_TEAM,
	PEOPLE_TIMESHEET_STATUS,
	PEOPLE_UNAVAILABILITY_KIND,
	PEOPLE_UNAVAILABILITY_STATUS,
	PEOPLE_VEHICLE_FINE_STATUS,
} from '../../constants';

const toInt = (value) => {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	if (typeof value === 'number' && Number.isFinite(value)) {
		return value;
	}

	const parsed = Number.parseInt(String(value).trim(), 10);
	return Number.isFinite(parsed) ? parsed : null;
};

const LEGACY_PEOPLE_TEAM_VALUES = new Set([1, 2, 3, 4, 5]);
const PEOPLE_GENDER_VALUES = new Set(['female', 'male', 'preferNotToSay']);

export const normalizePeopleEmployeeStatus = (status, employee) => {
	const directInt = toInt(status);

	if (directInt === PEOPLE_EMPLOYEE_STATUS.ARCHIVED) {
		return PEOPLE_EMPLOYEE_STATUS.TERMINATED;
	}

	// Current People values: reuse shared STATUS codes via PEOPLE_EMPLOYEE_STATUS.
	if (directInt !== null && Object.values(PEOPLE_EMPLOYEE_STATUS).includes(directInt)) {
		return directInt;
	}

	// Best-effort fallback for missing/invalid values.
	if (employee?.terminationDate || employee?.terminationReason) {
		return PEOPLE_EMPLOYEE_STATUS.TERMINATED;
	}

	if (employee?.user || employee?.onboardedAt) {
		return PEOPLE_EMPLOYEE_STATUS.ACTIVE;
	}

	return PEOPLE_EMPLOYEE_STATUS.DRAFT;
};

export const normalizePeopleEmploymentType = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_EMPLOYMENT_TYPE).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleGender = (value) => {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	const normalized = String(value).trim();
	return PEOPLE_GENDER_VALUES.has(normalized) ? normalized : null;
};

export const normalizePeopleTeam = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_TEAM).includes(directInt)) {
		return directInt;
	}

	// Legacy People team ids are intentionally collapsed to Operations after the 2026 team refresh.
	if (directInt !== null && LEGACY_PEOPLE_TEAM_VALUES.has(directInt)) {
		return PEOPLE_TEAM.OPERATIONS;
	}

	return null;
};

export const normalizePeopleTeams = (values) => {
	const normalizedValues = Array.isArray(values)
		? values.map(value => normalizePeopleTeam(value)).filter(value => value !== null)
		: [];

	return Array.from(new Set(normalizedValues));
};

export const normalizePeopleCompanyDocumentCategory = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_COMPANY_DOCUMENT_CATEGORY).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleEmployeeEventType = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_EMPLOYEE_EVENT_TYPE).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleEmployeeEventReviewStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_EMPLOYEE_EVENT_REVIEW_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const isPeopleEmployeeEventReviewRequired = (value) => {
	const normalizedType = normalizePeopleEmployeeEventType(value);
	return PEOPLE_EMPLOYEE_EVENT_REVIEW_REQUIRED_TYPES.includes(normalizedType);
};

export const normalizePeopleEmploymentChangeType = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_EMPLOYMENT_CHANGE_TYPE).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleEmploymentChangeStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_EMPLOYMENT_CHANGE_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleEmploymentChangeDocumentMode = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_EMPLOYMENT_CHANGE_DOCUMENT_MODE).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleEmployeeWarningType = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_EMPLOYEE_WARNING_TYPE).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleEmployeeWarningStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_EMPLOYEE_WARNING_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleVehicleFineStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_VEHICLE_FINE_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeoplePerformanceReviewType = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_PERFORMANCE_REVIEW_TYPE).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeoplePerformanceReviewStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_PERFORMANCE_REVIEW_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeoplePerformanceReviewOutcome = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_PERFORMANCE_REVIEW_OUTCOME).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleRosterArea = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_ROSTER_AREA).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleRosterShiftStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_ROSTER_SHIFT_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleOpenShiftApplicationStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_OPEN_SHIFT_APPLICATION_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleTimesheetStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_TIMESHEET_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleUnavailabilityKind = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_UNAVAILABILITY_KIND).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleUnavailabilityStatus = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_UNAVAILABILITY_STATUS).includes(directInt)) {
		return directInt;
	}

	return null;
};

export const normalizePeopleLeaveType = (value) => {
	const directInt = toInt(value);
	if (directInt !== null && Object.values(PEOPLE_LEAVE_ENTITLEMENT).includes(directInt)) {
		return directInt;
	}

	return null;
};
